import { Link } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import { Alert, Button, Card } from "@/shared/ui";
import type { AvailableRefillRequest } from "../models";

type RefillDisabledAlertProps = Pick<
  AvailableRefillRequest,
  "isRefillable" | "hasUpcomingAppointment" | "isSyncRequired"
>;

const getAlertTexts = ({
  isRefillable,
  hasUpcomingAppointment,
  isSyncRequired,
}: RefillDisabledAlertProps) => {
  if (hasUpcomingAppointment) {
    return {
      title: t`Upcoming appointment`,
      description: t`You have an upcoming visit with your provider. Please wait until after it before requesting another refill.`,
    };
  }

  if (isSyncRequired) {
    return {
      title: t`Video visit required`,
      description: t`A video visit with a provider is required before your next refill can be processed.`,
    };
  }

  if (!isRefillable) {
    return {
      title: t`Back to back orders`,
      description: t`Your last order was placed too recently. Please wait a little longer before requesting another refill.`,
    };
  }

  return {
    title: "",
    description: "",
  };
};

export const RefillDisabledAlert = (props: RefillDisabledAlertProps) => {
  const { title, description } = getAlertTexts(props);
  const { isSyncRequired, hasUpcomingAppointment } = props;

  if (isSyncRequired && !hasUpcomingAppointment) {
    return (
      <Card.Root className="mt-2 -ml-6 gap-1">
        <Card.Title>{title}</Card.Title>

        <Card.Description>{description}</Card.Description>

        <Button variant="secondary" className="cursor-pointer!" asChild>
          <Link to={ROUTES.CONSULTATIONS.OVERVIEW}>{t`Schedule now`}</Link>
        </Button>
      </Card.Root>
    );
  }

  return (
    <Alert type="info" className="mt-2 -ml-6 cursor-auto opacity-100">
      <h6 className="font-semibold">{title}</h6>
      <p>{description}</p>
    </Alert>
  );
};
